import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Loader } from 'lucide-react';
import { API_URL } from '../config';

const ForecastWidget = ({ accountId, metric = 'cpl', compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    fetch(`${API_URL}/forecast/${accountId}?metric=${metric}&days=7`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [accountId, metric]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader className="w-4 h-4 text-zinc-600 animate-spin" />
      </div>
    );
  }

  if (error || !data || (!data.historical?.length && !data.forecast?.length)) {
    return null;
  }

  const isImproving = data.trend === 'improving' || (metric !== 'spend' && metric !== 'leads' && data.pct_change_forecast < 0);
  const isDegrading = data.trend === 'degrading' || (metric !== 'spend' && metric !== 'leads' && data.pct_change_forecast > 5);
  const trendColor = isImproving ? '#22c55e' : isDegrading ? '#ef4444' : '#f59e0b';
  const trendBg = isImproving ? 'bg-green-500/10 text-green-400 border-green-500/20'
    : isDegrading ? 'bg-red-500/10 text-red-400 border-red-500/20'
    : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';

  const TrendIcon = isImproving ? TrendingDown : isDegrading ? TrendingUp : Minus;

  // Combine historical + forecast for the chart
  const chartData = [
    ...data.historical.map(h => ({ date: h.date.slice(5), value: h.value, type: 'historical' })),
    ...data.forecast.map(f => ({ date: f.date.slice(5), forecast: f.value, lower: f.lower, upper: f.upper, type: 'forecast' })),
  ];

  const splitIdx = data.historical.length;
  const metricLabel = { cpl: 'CPL', spend: 'Spend', leads: 'Leads', cpc: 'CPC' }[metric] || metric.toUpperCase();
  const currVal = data.current_value;
  const forecastVal = data.forecast?.[data.forecast.length - 1]?.value;
  const pct = data.pct_change_forecast;

  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium ${trendBg}`}>
        <TrendIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-mono">
          {metricLabel} {pct !== undefined ? `${pct > 0 ? '+' : ''}${pct}%` : ''}
          <span className="opacity-60 ml-1">7d</span>
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">7-Day Forecast · {metricLabel}</span>
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold ${trendBg}`}>
          <TrendIcon className="w-3 h-3" />
          <span>{pct !== undefined ? `${pct > 0 ? '+' : ''}${pct}%` : data.trend}</span>
        </div>
      </div>

      <div className="h-[60px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`hist-${accountId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={trendColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={trendColor} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={`fore-${accountId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={trendColor} stopOpacity={0.15} />
                <stop offset="95%" stopColor={trendColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <YAxis hide domain={['auto', 'auto']} />
            <Tooltip
              contentStyle={{ background: '#09090B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }}
              labelStyle={{ color: '#71717a' }}
              formatter={(v) => [v ? `₹${v}` : '-', '']}
            />
            {/* Historical area */}
            <Area
              type="monotone"
              dataKey="value"
              stroke={trendColor}
              strokeWidth={1.5}
              fill={`url(#hist-${accountId})`}
              dot={false}
              connectNulls
            />
            {/* Forecast area (dashed) */}
            <Area
              type="monotone"
              dataKey="forecast"
              stroke={trendColor}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill={`url(#fore-${accountId})`}
              dot={false}
              connectNulls
              opacity={0.7}
            />
            {splitIdx > 0 && (
              <ReferenceLine x={chartData[splitIdx - 1]?.date} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {forecastVal !== undefined && (
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-zinc-600">Now: <span className="text-zinc-400 font-mono">₹{currVal}</span></span>
          <span className="text-[10px] text-zinc-600">7d pred: <span className={`font-mono font-bold ${isImproving ? 'text-green-400' : isDegrading ? 'text-red-400' : 'text-yellow-400'}`}>₹{forecastVal}</span></span>
        </div>
      )}
    </div>
  );
};

export default ForecastWidget;
